import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { ProfileCardWrapper } from '@confluence/profile/entry-points/ProfileCardWrapper.ts';

import type { RankedPagePersonOrAgent } from '../../utils/rankPagePeopleAndAgents.ts';
import { PersonRow } from '../pressable-group/PersonRow.tsx';

import { PersonListAvatar } from './PersonListAvatar.tsx';
import { usePersonProfileNavigation } from './usePersonProfileNavigation.ts';

const i18n = defineMessages({
	agentAttribution: {
		id: 'byline-connections.preview-panel.agent.attribution',
		defaultMessage: 'with {name}',
		description:
			'Subtitle below an agent in the Teamwork Graph panel and modal. Name is the person the agent contributed to or viewed this page on behalf of.',
	},
});

const relationshipMessages = defineMessages({
	created: {
		id: 'byline-connections.preview-panel.person.relationship.created',
		defaultMessage: 'Creator of this page',
		description:
			'Subtitle below a person in the Teamwork Graph panel and modal. This person created the current page.',
	},
	contributed: {
		id: 'byline-connections.preview-panel.person.relationship.contributed',
		defaultMessage: 'Contributor of this page',
		description:
			'Subtitle below a person in the Teamwork Graph panel and modal. This person contributed to the current page.',
	},
	owns: {
		id: 'byline-connections.preview-panel.person.relationship.owns',
		defaultMessage: 'Owner of this page',
		description:
			'Subtitle below a person in the Teamwork Graph panel and modal. This person owns the current page.',
	},
	updated: {
		id: 'byline-connections.preview-panel.person.relationship.updated',
		defaultMessage: 'Editor of this page',
		description:
			'Subtitle below a person in the Teamwork Graph panel and modal. This person updated the current page.',
	},
	viewed: {
		id: 'byline-connections.preview-panel.person.relationship.viewed',
		defaultMessage: 'Viewer of this page',
		description:
			'Subtitle below a person in the Teamwork Graph panel and modal. This person viewed the current page.',
	},
	favorited: {
		id: 'byline-connections.preview-panel.person.relationship.favorited',
		defaultMessage: 'Favoriter of this page',
		description:
			'Subtitle below a person in the Teamwork Graph panel and modal. This person favorited the current page.',
	},
	liked: {
		id: 'byline-connections.preview-panel.person.relationship.liked',
		defaultMessage: 'Liker of this page',
		description:
			'Subtitle below a person in the Teamwork Graph panel and modal. This person liked the current page.',
	},
	'mentioned-in': {
		id: 'byline-connections.preview-panel.person.relationship.mentioned-in',
		defaultMessage: 'Mentionee of this page',
		description:
			'Subtitle below a person in the Teamwork Graph panel and modal. This person is mentioned in the current page.',
	},
	snapshotted: {
		id: 'byline-connections.preview-panel.person.relationship.snapshotted',
		defaultMessage: 'Snapshot creator of this page',
		description:
			'Subtitle below a person in the Teamwork Graph panel and modal. This person created a snapshot of the current page.',
	},
	watches: {
		id: 'byline-connections.preview-panel.person.relationship.watches',
		defaultMessage: 'Watcher of this page',
		description:
			'Subtitle below a person in the Teamwork Graph panel and modal. This person watches the current page.',
	},
	'shared-with': {
		id: 'byline-connections.preview-panel.person.relationship.shared-with',
		defaultMessage: 'Recipient of this page',
		description:
			'Subtitle below a person in the Teamwork Graph panel and modal. This person was given access through sharing of the current page.',
	},
});

export const PersonListRow = ({
	personOrAgent,
}: {
	personOrAgent: RankedPagePersonOrAgent;
}): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const attributedUser = personOrAgent.agent?.attributedUser;

	const onClick = usePersonProfileNavigation(
		personOrAgent.accountId,
		personOrAgent.primaryRelationship,
	);

	const row = (
		<PersonRow
			avatar={<PersonListAvatar personOrAgent={personOrAgent} />}
			name={personOrAgent.name}
			role={
				attributedUser?.name.trim()
					? formatMessage(i18n.agentAttribution, {
							name: attributedUser.name,
						})
					: formatMessage(relationshipMessages[personOrAgent.primaryRelationship])
			}
			// Agent data supplies no profile destination, so these rows stay passive.
			onClick={personOrAgent.agent ? undefined : onClick}
			// Keyed on the ARI, the only id `parsePagePeople` guarantees is present.
			testId={`teamwork-graph-person-row-${personOrAgent.ari}`}
		/>
	);

	if (personOrAgent.agent) {
		return row;
	}

	return (
		<ProfileCardWrapper
			userId={personOrAgent.accountId}
			fullName={personOrAgent.name}
			trigger="hover"
			position="left-start"
			fullWidthTrigger
			// The row button owns focus and its accessible name; the card handles bubbled keys.
			disabledAriaAttributes
		>
			{row}
		</ProfileCardWrapper>
	);
};
